import React, { useState } from "react";
import {
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import AppLayout from "@amzn/awsui-components-react/polaris/app-layout";
import {
  Alert,
  Box,
  ContentLayout,
  Header,
  Select,
  SelectProps,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import { DashboardType, QuickSightDashboard } from "./QuicksightDashboard";
import PageHeader from "../PageHeader";
import TrackStarsLaunch from "../common/TrackStarsLaunch";
import { StageContext } from "../../contexes/OrderIdContext";
import { OmsProduct, Stage } from "../../lusca-search-api/generated-src";
import OMSReportDownload from "./OMSReportDownload";
import {} from "../../constants";
import useDocumentTitle from "../../hooks/useDocumentTitle";

const TVOD_UNAVAILABLE_REASON =
  "The current TVOD report has no data. It will be re-enabled once account coverage expansion is complete.";

// Maps the `/dashboard/oms/:product` path segment to its backend dashboard type.
const PRODUCTS: {
  value: OmsProduct;
  label: string;
  dashboardType: DashboardType;
  unavailableReason?: string;
}[] = [
  {
    value: OmsProduct.Channels,
    label: "PV Channels",
    dashboardType: "pv_channels",
  },
  { value: OmsProduct.Addon, label: "PV Addon", dashboardType: "pv_addon" },
  {
    value: OmsProduct.Tvod,
    label: "PV TVOD",
    dashboardType: "pv_tvod",
    unavailableReason: TVOD_UNAVAILABLE_REASON,
  },
];

const BASE_PATH = "/dashboard/oms";

const OMSDashboard: React.FC = () => {
  const { product: productParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [, setShareHandler] = useState<(() => Promise<string>) | null>(null);

  // No product selected (bare /dashboard/oms) shows only the selector.
  const product = PRODUCTS.find((p) => p.value === productParam) || null;
  useDocumentTitle(
    product ? `${product.label} | PV OMS Dashboard` : "PV OMS Dashboard"
  );

  // An unrecognized product segment falls back to the default page.
  if (productParam && !PRODUCTS.some((p) => p.value === productParam)) {
    return <Navigate to={BASE_PATH} replace />;
  }

  const handleProductChange = (value: string) => {
    const selectedProduct = PRODUCTS.find((p) => p.value === value);
    if (!selectedProduct || selectedProduct.unavailableReason) return;

    // Filters are dashboard-specific, so drop them when switching products.
    const params = new URLSearchParams(searchParams);
    params.delete("filters");
    const search = params.toString();
    navigate(`${BASE_PATH}/${value}${search ? `?${search}` : ""}`);
  };

  const selectOptions: SelectProps.Option[] = PRODUCTS.map((p) => ({
    value: p.value,
    label: p.label,
    disabled: !!p.unavailableReason,
    disabledReason: p.unavailableReason,
    description: p.unavailableReason
      ? "Awaiting account coverage expansion"
      : undefined,
  }));

  return (
    <StageContext.Provider value={Stage.Prod}>
      <PageHeader />
      <AppLayout
        content={
          <ContentLayout
            defaultPadding
            header={
              <div className="oms-dashboard-header">
                <div className="oms-dashboard-header__title">
                  <Header variant="h1">PV OMS Dashboard</Header>
                </div>
                <div>
                  <TrackStarsLaunch product="oms" />
                </div>
                <div className="oms-dashboard-header__selector">
                  <Select
                    selectedOption={
                      product
                        ? { value: product.value, label: product.label }
                        : null
                    }
                    onChange={({ detail }) =>
                      handleProductChange(detail.selectedOption.value as string)
                    }
                    options={selectOptions}
                    placeholder="Select a dashboard"
                    ariaLabel="Select PV OMS dashboard"
                  />
                </div>
              </div>
            }
          >
            <SpaceBetween size="xl">
              {product && !product.unavailableReason && (
                <OMSReportDownload
                  key={product.value}
                  product={product.value}
                />
              )}
              {product?.unavailableReason && (
                <Alert
                  type="info"
                  header={`${product.label} temporarily unavailable`}
                >
                  {product.unavailableReason}
                </Alert>
              )}
              {product && !product.unavailableReason && (
                <QuickSightDashboard
                  stage={Stage.Prod}
                  dashboardType={product.dashboardType}
                  searchParams={searchParams}
                  setSearchParams={setSearchParams}
                  setShareHandler={setShareHandler}
                />
              )}
              {!product && (
                <Box
                  padding="l"
                  color="text-status-inactive"
                  textAlign="center"
                >
                  Select a dashboard from the dropdown above to get started.
                </Box>
              )}
            </SpaceBetween>
          </ContentLayout>
        }
        contentType="default"
        headerSelector="#h"
        maxContentWidth={1700}
        navigationHide
        toolsHide
      />
    </StageContext.Provider>
  );
};

export default OMSDashboard;
